import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, ViewChild, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService, LangChangeEvent, TranslateModule } from '@ngx-translate/core';
import { Subscription, forkJoin, map } from 'rxjs';
import { NgbAccordionModule, NgbAccordionDirective } from '@ng-bootstrap/ng-bootstrap';
import { BookingPartChooseSpecialistDumbComponent } from '../choose-specialist/choose-specialist.dumb.component';
import { IEmployee, ITreatmentApiModel } from '@app/shared/models';
import { ControlContainer, FormBuilder, FormGroupDirective, ReactiveFormsModule } from '@angular/forms';
import { BookingStepsFormType } from '@app/shared/models/booking-steps-form-type.model';
import { EmployeesService } from '@app/shared/services/employees/employees.service';
import { TreatmentAssingementsFormGroup } from '@app/shared/models/treatment-assignments-form-group.model';
import { TreatmentAssingmentArray } from '@app/shared/models/treatment-assingment-array.model';

@Component({
  selector: 'app-choose-treatments-and-employees',
  templateUrl: './choose-treatments-and-employees.smart.component.html',
  styleUrls: ['./choose-treatments-and-employees.smart.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    NgbAccordionModule,
    BookingPartChooseSpecialistDumbComponent,
    ReactiveFormsModule],
  viewProviders: [{
    provide: ControlContainer,
    useExisting: FormGroupDirective
  }]
})
export class BookingPartChooseTreatmentsAndEmployeeSmartComponent implements OnInit, OnDestroy {
  @ViewChild(NgbAccordionDirective) accordionRef: NgbAccordionDirective;
  public parentForm: BookingStepsFormType;
  public isSpecifiedEmployee: boolean;
  public employeeName: string;
  public employeeImageUrl: string;
  public isAnyEmployee: boolean;
  public isAutomaticallySelectedEmployee: boolean;
  public currentLang: string;
  public employees: IEmployee[] = [];
  public assignmentsArray: TreatmentAssingmentArray[] = [];
  public assingmentsArray1: {
    employee: IEmployee;
    treatment: ITreatmentApiModel;
  }[] = [];

  public displayNameForAnyEmployee = 'BOOKING-PART.STEPS.CHOOSE-SPECIALIST.ANY-SPECIALIST';
  public photoUrlForAnyEmployee = 'assets/img/any-employee.svg';
  public isCollapsed: boolean;

  public readonly parentFormDirective = inject(FormGroupDirective);
  private readonly employeesService = inject(EmployeesService);
  private readonly translateService = inject(TranslateService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  private readonly fb = inject(FormBuilder);

  private masterSubscription = new Subscription();

  ngOnInit(): void {
    this.parentForm = this.parentFormDirective.form;
    this.fetchEmployees();
    this.currentLang = this.translateService.currentLang;
    const sub2 = this.translateService.onLangChange
      .subscribe((event: LangChangeEvent) => {
        this.currentLang = event.lang;
      });
    this.masterSubscription.add(sub2);
  }

  ngOnDestroy(): void {
    this.masterSubscription.unsubscribe();
  }

  public collapseAccordion(id: string): void {
    this.accordionRef.collapse(id);
  }

  public onTreatmentChangeClick(): void {
    this.router.navigate(['../'], { relativeTo: this.route });
  }

  public get treatments(): ITreatmentApiModel[] {
    return this.parentForm.controls.treatments.value;
  }

  private chooseAutomaticallyForOnlyOneEmployee(employees: IEmployee[]): IEmployee | null {
    if (employees.length === 1) {
      return employees[0];
    } else {
      return null;
    }
  }

  private fetchEmployees(): void {
    const observables = this.parentForm.controls.treatments.value.map((treatment) => this.employeesService.getSalonEmployees(
      this.parentForm.controls.salonId.value,
      treatment.id
    ).pipe(
      map((employees: IEmployee[]) => ({ treatment, employees }))
    ));
    const sub = forkJoin(observables).subscribe((results: { treatment: ITreatmentApiModel, employees: IEmployee[] }[]) => {
      this.isCollapsed = results.length > 1;
      results.forEach((result, index) => {
        const { treatment, employees } = result;
        this.assignmentsArray.push(this.createFullListOfEmployeesPerTreatment(treatment, employees));
        this.parentForm.controls.treatmentAssingments.push(this.assignEmployeeToTreatment(treatment, employees), { emitEvent: index === results.length - 1 });
      });
    });
    this.masterSubscription.add(sub);
  }

  private assignEmployeeToTreatment(treatment: ITreatmentApiModel, employees: IEmployee[]): TreatmentAssingementsFormGroup {
    return this.fb.group({
      treatment,
      employee: this.chooseAutomaticallyForOnlyOneEmployee(employees),
      id: treatment.id,
      isCollapsed: this.isCollapsed || employees.length <= 1
    });
  }

  private createFullListOfEmployeesPerTreatment(treatment: ITreatmentApiModel, employees: IEmployee[]): TreatmentAssingmentArray {
    return {
      treatment,
      employees
    };
  }
}
